import apiClient from './apiClient';
import { MedicalHistory, CreateMedicalHistoryRequest, UpdateMedicalHistoryRequest, MedicalDocument } from '../types/MedicalHistory';

export const medicalHistoryService = {
  getAllMedicalHistories: async (): Promise<MedicalHistory[]> => {
    const response = await apiClient.get('/medicalhistory');
    return response.data;
  },

  getMedicalHistoryById: async (id: number): Promise<MedicalHistory> => {
    const response = await apiClient.get(`/medicalhistory/${id}`);
    return response.data;
  },

  getMedicalHistoriesByPatient: async (patientId: number): Promise<MedicalHistory[]> => {
    const response = await apiClient.get(`/medicalhistory/patient/${patientId}`);
    return response.data;
  },

  getLatestMedicalHistoryByPatient: async (patientId: number): Promise<MedicalHistory> => {
    const response = await apiClient.get(`/medicalhistory/patient/${patientId}/latest`);
    return response.data;
  },

  getMedicalHistoriesByDoctor: async (doctorName: string): Promise<MedicalHistory[]> => {
    const response = await apiClient.get(`/medicalhistory/doctor/${encodeURIComponent(doctorName)}`);
    return response.data;
  },

  getMedicalHistoriesByDiagnosis: async (diagnosis: string): Promise<MedicalHistory[]> => {
    const response = await apiClient.get(`/medicalhistory/diagnosis/${encodeURIComponent(diagnosis)}`);
    return response.data;
  },

  getMedicalHistoriesByDateRange: async (startDate: string, endDate: string): Promise<MedicalHistory[]> => {
    const response = await apiClient.get(`/medicalhistory/date-range?startDate=${startDate}&endDate=${endDate}`);
    return response.data;
  },

  createMedicalHistory: async (medicalHistory: CreateMedicalHistoryRequest): Promise<MedicalHistory> => {
    // Convert date strings to ISO format for API
    const payload = {
      ...medicalHistory,
      visitDate: medicalHistory.visitDate ? new Date(medicalHistory.visitDate).toISOString() : new Date().toISOString(),
      nextVisitDate: medicalHistory.nextVisitDate ? new Date(medicalHistory.nextVisitDate).toISOString() : null,
    };
    const response = await apiClient.post('/medicalhistory', payload);
    return response.data;
  },

  updateMedicalHistory: async (id: number, medicalHistory: UpdateMedicalHistoryRequest): Promise<MedicalHistory> => {
    // Convert date strings to ISO format for API
    const payload = {
      ...medicalHistory,
      visitDate: medicalHistory.visitDate ? new Date(medicalHistory.visitDate).toISOString() : new Date().toISOString(),
      nextVisitDate: medicalHistory.nextVisitDate ? new Date(medicalHistory.nextVisitDate).toISOString() : null,
    };
    const response = await apiClient.put(`/medicalhistory/${id}`, payload);
    return response.data;
  },

  deleteMedicalHistory: async (id: number): Promise<void> => {
    await apiClient.delete(`/medicalhistory/${id}`);
  },

  // Document management
  uploadDocument: async (medicalHistoryId: number, file: File, description?: string): Promise<MedicalDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }
    const response = await apiClient.post(`/medicalhistory/${medicalHistoryId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getDocuments: async (medicalHistoryId: number): Promise<MedicalDocument[]> => {
    const response = await apiClient.get(`/medicalhistory/${medicalHistoryId}/documents`);
    return response.data;
  },

  downloadDocument: async (documentId: number): Promise<Blob> => {
    const response = await apiClient.get(`/medicalhistory/documents/${documentId}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  deleteDocument: async (documentId: number): Promise<void> => {
    await apiClient.delete(`/medicalhistory/documents/${documentId}`);
  },

  getDocumentUrl: (documentId: number): string => {
    return `${apiClient.defaults.baseURL}/medicalhistory/documents/${documentId}`;
  },
};